/**
 * THE PEOPLE, AND EVERYTHING THAT HANGS OFF ONE OF THEM.
 *
 * Three and a half thousand of them, which is the number that decides the
 * shape of this file. A list that fetches everything and filters in the
 * browser is fine at six and useless at 3,642, so the search happens in
 * the database and the page asks for a window.
 *
 * Phone search goes through `phone10`, the generated column. The old
 * system matched phone numbers five different ways — last four, last
 * seven, a regex inside a policy, a slice in JavaScript — which is how one
 * person ended up as four customers. There is one way here.
 */

import { db } from "./client.ts";

export type CustomerRow = {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  created_at: string;
  /** When anything about them last changed. What "most recent" means here. */
  updated_at: string | null;
};

export type Page<T> = { rows: T[]; total: number };

/**
 * ONE GROUP PER WORD, AND EVERY VALUE QUOTED.
 *
 * Anthony: "search function wasn't working." It was worse than not
 * working — it was returning **HTTP 400** on the shop's own data.
 *
 * TWO FAULTS, ONE LINE. The term went into a PostgREST `or()` raw:
 *
 *   or=(full_name.ilike.%Smith, Jarrod%, ...)
 *
 * A COMMA IS THE SEPARATOR in that syntax, so any name containing one
 * broke the query outright — and this shop's imported names are full of
 * them: "Medina,Erica N/A", "Aguilar,Marco N/A", "Silva,Nadine N/A". Copy
 * a name off the list, paste it into the box, and the search 400s. Round
 * brackets and a stray dot do the same thing.
 *
 * AND IT WAS ONE SUBSTRING, so word order had to match exactly. "erica
 * medina" found nothing while "medina" found her, which reads as a broken
 * box rather than a strict one.
 *
 * So: the value is DOUBLE QUOTED, which is how PostgREST is told that a
 * comma is part of the text, and the term is split into words with one
 * group per word. Repeated `or=` parameters are ANDed by PostgREST, so
 * every word has to appear somewhere — name, email or address — in any
 * order.
 */
const QUOTABLE = new RegExp('["' + String.fromCharCode(92, 92) + "]", "g");

export function nameFilters(term: string): string[] {
  return String(term ?? "")
    .split(/\s+/)
    // Punctuation hanging off a word is punctuation, not a search.
    // "Smith, Jarrod" pasted off the list splits to ["Smith,", "Jarrod"],
    // and "%Smith,%" matches nobody called Jarrod Smith.
    .map((w) => w.trim().replace(/^[^\p{L}\p{N}@]+|[^\p{L}\p{N}@]+$/gu, ""))
    .filter(Boolean)
    .slice(0, 4)                       // four words is a search, not a sentence
    .map((w) => {
      // Escaping the quote and the backslash is what keeps a value with a
      // comma in it a value rather than a new condition.
      const v = `"%${w.replace(QUOTABLE, (c) => String.fromCharCode(92) + c)}%"`;
      return `full_name.ilike.${v},email.ilike.${v},address.ilike.${v}`;
    });
}

/** Digits only, so "(951) 555-0201" and "9515550201" are the same search. */
const digits = (s: string) => s.replace(/\D/g, "");

export async function listCustomers(
  opts: { search?: string; limit?: number; offset?: number;
          orderColumn?: string; orderAscending?: boolean } = {},
): Promise<Page<CustomerRow>> {
  const sb = await db();
  const limit = opts.limit ?? 50;
  const offset = opts.offset ?? 0;
  const term = (opts.search ?? "").trim();

  /**
   * MOST RECENT MEANS LAST TOUCHED, NOT FIRST ADDED.
   *
   * Anthony: "sort customer tab on most recent with min/hour/day not just
   * days."
   *
   * It sorted and displayed `created_at`, which for a shop that imported
   * three and a half thousand people is the day of the import for nearly
   * all of them. So the list opened on whoever happened to be last in a
   * CSV and every row read "4 months", and a customer who rang an hour ago
   * was somewhere in the middle of it.
   *
   * `updated_at` moves whenever anything about them changes, which is the
   * closest thing the record has to "we dealt with these people recently".
   */
  let q = sb.from("customers")
    .select("id,full_name,phone,email,address,created_at,updated_at", { count: "exact" })
    .order(opts.orderColumn ?? "updated_at",
      { ascending: opts.orderAscending ?? false, nullsFirst: false })
    .range(offset, offset + limit - 1);

  if (term) {
    const d = digits(term);
    // A search that looks like a phone number is one; anything else is a
    // name or an address. Asking both questions of every term returns a
    // customer called "0" for the search "0".
    if (d.length >= 3) {
      q = q.like("phone10", `%${d}%`);
    } else {
      for (const group of nameFilters(term)) q = q.or(group);
    }
  }

  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as CustomerRow[], total: count ?? 0 };
}

export type CustomerWhole = {
  customer: CustomerRow & { notes: string | null; source: string | null };
  vehicles: Array<{ id: string; year: number | null; make: string | null; model: string | null;
                    description: string | null; license_plate: string | null; mileage: number | null }>;
  jobs: Array<{ id: string; job_number: string; status: string; title: string | null; created_at: string }>;
  invoices: Array<{ id: string; invoice_number: string; status: string; total: number; issued_at: string }>;
};

export async function getCustomer(id: string): Promise<CustomerWhole | null> {
  const sb = await db();
  const [c, v, j, i] = await Promise.all([
    sb.from("customers").select("id,full_name,phone,email,address,notes,source,created_at")
      .eq("id", id).maybeSingle(),
    sb.from("vehicles").select("id,year,make,model,description,license_plate,mileage")
      .eq("customer_id", id).is("archived_at", null).order("created_at", { ascending: false }),
    sb.from("jobs").select("id,job_number,status,title,created_at")
      .eq("customer_id", id).order("created_at", { ascending: false }).limit(50),
    sb.from("invoices").select("id,invoice_number,status,total,issued_at")
      .eq("customer_id", id).order("issued_at", { ascending: false }).limit(50),
  ]);
  if (!c.data) return null;
  return {
    customer: c.data as CustomerWhole["customer"],
    vehicles: (v.data ?? []) as CustomerWhole["vehicles"],
    jobs: (j.data ?? []) as CustomerWhole["jobs"],
    invoices: (i.data ?? []) as CustomerWhole["invoices"],
  };
}

/**
 * One field, changed in place.
 *
 * A column allow-list, not because the caller is untrusted — they are the
 * signed-in user and row level security already decides which shop's rows
 * they can touch — but because a typo in a column name should be a refusal
 * here rather than a PostgREST error surfacing in the interface.
 */
const CUSTOMER_FIELDS = new Set(["full_name", "phone", "email", "address", "notes"]);

export async function updateCustomerField(id: string, column: string, value: string): Promise<void> {
  if (!CUSTOMER_FIELDS.has(column)) throw new Error(`${column} is not a field you can change here.`);
  if (column === "full_name" && !value.trim()) throw new Error("A customer needs a name.");
  const sb = await db();
  const { error } = await sb.from("customers").update({ [column]: value.trim() || null }).eq("id", id);
  if (error) throw new Error(error.message);
}
